/**
 * Which billing screen a visitor should see, as a value.
 *
 * Pulled out of the panel because the states are the hard part and the DOM is
 * not. Eight of them, and the ones that matter most are the ones nobody
 * remembers to build: signed out, mid-activation, and a subscription that has
 * already been cancelled but has not ended yet.
 *
 * Pure, so every branch is covered in the node suite. There is no DOM test
 * environment here and this is the logic worth testing.
 */
import { limitsFor, TIER_NAMES, type Tier } from "../../shared/tiers";
import type { Identity, SubscriptionState } from "../auth/session";

export type BillingView =
  /** `/api/me` has not answered yet. Show nothing rather than a wrong plan. */
  | { kind: "loading" }
  /** No account. Plans are visible; buying one needs signing in first. */
  | { kind: "signed-out" }
  /** An account, no subscription. */
  | { kind: "free" }
  /** Paying, and nothing is wrong. */
  | { kind: "subscribed"; tier: Tier; renewsAt: number | null }
  /** Paying, but the last payment failed and Stripe is retrying. */
  | { kind: "past-due"; tier: Tier }
  /** Cancelled, still inside the period already paid for. */
  | { kind: "ending"; tier: Tier; endsAt: number }
  /** Subscription is over. Everything they made is still there. */
  | { kind: "lapsed" };

/**
 * The order of these checks is the whole design.
 *
 * `loading` first, because rendering "Free" to a Creator for one frame and then
 * correcting it reads as a glitch and, worse, briefly shows an upgrade prompt
 * to somebody who has already paid.
 *
 * `past-due` before `subscribed`, because a failing card is the thing they need
 * to know and the plan name is not. `ending` before `subscribed` for the same
 * reason: "renews on the 9th" is wrong if it does not.
 */
export function billingView(identity: Identity, ready: boolean): BillingView {
  if (!ready) return { kind: "loading" };
  if (!identity.signedIn && !identity.devUser) return { kind: "signed-out" };

  const sub: SubscriptionState = identity.subscription;

  if (sub.pastDue) return { kind: "past-due", tier: identity.tier };

  if (sub.cancelAtPeriodEnd && sub.currentPeriodEnd) {
    return { kind: "ending", tier: identity.tier, endsAt: sub.currentPeriodEnd };
  }

  // A tier that has fallen back to free after a cancellation reads differently
  // from someone who has simply never subscribed — they have work in here.
  if (identity.tier === "free") {
    return sub.status === "canceled" || sub.status === "unpaid"
      ? { kind: "lapsed" }
      : { kind: "free" };
  }

  return { kind: "subscribed", tier: identity.tier, renewsAt: sub.currentPeriodEnd };
}

export interface Headline {
  title: string;
  detail: string;
  /** Rendered as a warning rather than as body text. */
  urgent?: boolean;
}

/**
 * What the top of the page says.
 *
 * Direct, and never a label that restates the heading. "Plan: Creator" under a
 * heading that says Creator is a row of pixels that tells nobody anything.
 */
export function headline(view: BillingView, formatDate: (ms: number) => string): Headline {
  switch (view.kind) {
    case "loading":
      return { title: "", detail: "" };

    case "signed-out":
      return {
        title: "Pick a plan",
        // The reason to make an account, stated as a benefit rather than as a
        // requirement. "You must sign in" is true and persuades nobody.
        detail: "Sign in to subscribe. Your designs follow you to any computer you use.",
      };

    case "free":
      return {
        title: "Free",
        // Says something the rest of the page does not. The usage line already
        // gives the count, and the card below already lists what upgrading
        // buys — so this is the place to say what free is not missing, which
        // is the part that makes the paid tier a choice rather than a wall.
        detail: "Clean edges and the watermark tool are in every plan, including this one.",
      };

    case "subscribed":
      return {
        title: TIER_NAMES[view.tier],
        detail: view.renewsAt ? `Renews ${formatDate(view.renewsAt)}.` : "Active.",
      };

    case "past-due":
      return {
        title: TIER_NAMES[view.tier],
        // Says what happened, what it has not cost them yet, and what fixes it.
        detail:
          "Your last payment did not go through. Update your card to stay on this plan — nothing has changed yet.",
        urgent: true,
      };

    case "ending":
      return {
        title: TIER_NAMES[view.tier],
        // The second sentence is the one people actually want, and it is the
        // reason cancelling can be one click.
        detail: `Ends ${formatDate(view.endsAt)}, then Free. Your designs stay saved.`,
      };

    case "lapsed":
      return {
        title: "Free",
        detail: "Everything you made is still here. Resubscribe to edit all of it again.",
      };
  }
}

/**
 * What this account is using, against what it is allowed.
 *
 * "Designs 0" and "Storage 0 B" were two labels with no denominator — a number
 * with nothing to compare it to, which is why they read as debug output. The
 * quota is the entire meaning: "0 of 3" says both where you are and what
 * upgrading buys, in five characters.
 */
export function usageLine(
  tier: Tier,
  projectCount: number,
  storageBytes: number,
  formatBytes: (n: number) => string,
): string {
  const limits = limitsFor(tier);
  return `${projectCount} of ${limits.maxProjects} designs · ${formatBytes(storageBytes)} of ${formatBytes(limits.maxStorageBytes)}`;
}

/** Does this view offer the Stripe portal? Only a real customer has one. */
export function hasPortal(view: BillingView): boolean {
  return view.kind === "subscribed" || view.kind === "past-due" || view.kind === "ending";
}

/** Does this view offer plans to buy? */
export function showsPlans(view: BillingView): boolean {
  return view.kind === "signed-out" || view.kind === "free" || view.kind === "lapsed";
}
